import { HttpParams } from '@angular/common/http';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { Location } from 'src/app/models/configurators-2d/location';
import { Picture } from 'src/app/models/configurators-2d/picture';
import { Project } from 'src/app/models/project';
import { IAdministrationService } from './administrations/i-administration-service';
import { BaseApiService } from './base-api-service';
import { MODAL } from '../modal.service';
import { Discount } from 'src/app/models/configurators-2d/administrations/discounts/discount';
import { Ean } from 'src/app/models/configurators-2d/administrations/discounts/ean';
import { Parameters } from 'src/app/models/configurators-2d/applications/carpentry/Elcia/parameters';
import { Cdm } from 'src/app/models/configurators-2d/applications/carpentry/cdm';
import { Order } from 'src/app/models/configurators-2d/order';
import { ShoppingCartProductList } from 'src/app/models/shopping-cart/shopping-cart-product-list';
import { ShoppingCartProduct } from 'src/app/models/shopping-cart/shopping-cart-product';
import { logError } from '../helpers/sentry.helper';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { parseUtcDateOrNull } from '../helpers/utc-date.helper';
import { ScreenSizeDetectionService } from '../screen-detection.service';
import { FeatureSettingsService } from '../feature-settings/feature-settings.service';

export abstract class CommonApp2dService extends BaseApiService implements IAdministrationService {
  private readonly translateService = inject(TranslateService);
  private readonly screenDetection = inject(ScreenSizeDetectionService);
  private readonly commonFeatureSettingsService = inject(FeatureSettingsService);

  constructor(protected applicationName: CONFIG_CONTEXT) {
    super(applicationName);
  }

  public saveProject(configuration: any): Promise<boolean> {
    if (configuration.id) {
      return this.put<boolean>(`${this.apiUrl}Configuration`, configuration);
    } else {
      return this.post<boolean>(`${this.apiUrl}Configuration`, configuration);
    }
  }

  /**
   * Fonction de récupération des projets client
   * @param id id du client
   * @returns liste des projets client
   */
  async getProjectsByUser(id: string): Promise<Project[]> {
    const params = new HttpParams().set('id', id);
    try {
      const res = await this.get<Project[]>(`${this.apiUrl}Configuration/Projects`, params);
      return res.map((project: any) => ({
        projectUUID: this.distribId + '_' + project.id,
        projectID: project.id,
        projectName: project.name,
        dateUpdate: parseUtcDateOrNull(project.lastModificationTime),
        search: project.name.toLowerCase(),
        thumb: project.hasThumbnail ? project.picture : this.apiUrl + project.picture,
        distributionID: this.distribId,
        description: '',
        extraDatas: project.extraDatas,
        hasDiscount: project.hasDiscount,
        hasQuotation: project.hasQuotation,
        quotationExpirationDate: project.quotationExpirationDate,
        inTunnel: project.inTunnel,
        tunnelId: project.tunnelId,
        tunnelContext: project.tunnelContext,
        is_locked: project.is_locked,
      }));
    } catch (error) {
      logError(error);
      return [];
    }
  }

  /**
   * Fonction de duplication du projet
   * @param id id du projet
   * @param title titre du nouveau projet
   * @returns configuration (typé en any pour l'utiliser aussi sur ClosetDoorsStandard)
   */
  duplicateProjectById(id: string, title: string): Promise<any> {
    const params = new HttpParams().set('id', id).set('title', title);
    return this.post(`${this.apiUrl}Configuration/Duplicate`, {}, params);
  }

  /**
   * Récupération du fichier XML
   * @param configId id du projet
   * @param contactedByPhoneOverride réponse déjà connue (ex. commande Workbench déclenchée en sous-commande d'un envoi Kitchen), pour éviter de redemander le consentement
   * @returns fichier XML, et le consentement effectivement transmis (le cas échéant)
   */
  async generateXML(
    configId: string,
    contactedByPhoneOverride?: boolean
  ): Promise<{ orderId: string; xml: string; contactedByPhone?: boolean }> {
    let params = new HttpParams().set('id', configId).set('isMobile', this.screenDetection.isMobile());
    const contactedByPhone = await this.resolveContactedByPhone(configId, contactedByPhoneOverride);

    if (contactedByPhone !== undefined) {
      params = params.set('contactedByPhone', contactedByPhone);
    }

    const res = await this.post<{ id: string, xmlBase64: string }>(`${this.apiUrl}Order`, {}, params);
    const bytes = Uint8Array.from(atob(res.xmlBase64), c => c.codePointAt(0));
    return { orderId: res.id, xml: new TextDecoder('utf-8').decode(bytes), contactedByPhone };
  }

  /**
   * Résout le consentement à transmettre pour ce projet : réponse déjà connue, sinon valeur
   * déjà enregistrée, sinon demande au vendeur. `undefined` pour un client professionnel, ou si
   * la fonctionnalité est désactivée via le réglage {@link FeatureSettings.contactedByPhone}.
   */
  private async resolveContactedByPhone(configId: string, override?: boolean): Promise<boolean | undefined> {
    if (!this.commonFeatureSettingsService.contactedByPhoneEnabled) {
      return undefined;
    }

    if (this.customer.userDatas?.customerType === 'Professional') {
      return undefined;
    }

    const contactedByPhone = override ?? (await this.getPersistedContactedByPhone(configId)) ?? (await this.askContactedByPhone());
    this.setPersistedContactedByPhone(configId, contactedByPhone);
    return contactedByPhone;
  }

  /**
   * Consentement déjà enregistré pour ce projet, si connu (mémoire de session, sinon base).
   * Surchargée par {@link BaseAppService}.
   */
  protected async getPersistedContactedByPhone(configId: string): Promise<boolean | undefined> {
    return undefined;
  }

  /**
   * Mémorise le consentement résolu pour ce projet, pour éviter de le redemander lors d'un
   * prochain envoi dans la même session. Surchargée par {@link BaseAppService}.
   */
  protected setPersistedContactedByPhone(configId: string, contactedByPhone: boolean): void {
    // no-op par défaut : rien à mettre à jour en mémoire tant qu'aucune configuration n'est suivie
  }

  /**
   * Demande au vendeur si le client accepte d'être contacté par téléphone.
   */
  private askContactedByPhone(): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      this.modalService.display(MODAL.GENERIC_CONFIRM, {
        message: this.translateService.instant('contacted-by-phone.question'),
        onValidate: () => {
          resolve(true);
          this.modalService.close();
        },
        onCancel: () => {
          resolve(false);
          this.modalService.close();
        },
      });
    });
  }

  async getPrevisit(configId: string): Promise<string> {
    let params = new HttpParams().set('id', configId);
    const contactedByPhone = await this.resolveContactedByPhone(configId);

    if (contactedByPhone !== undefined) {
      params = params.set('contactedByPhone', contactedByPhone);
    }

    const res = await this.post(`${this.apiUrl}Order/Previsite`, {}, params);
    const bytes = Uint8Array.from(atob(res.xmlBase64), c => c.codePointAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  }

  /** Consentement du client à être contacté par téléphone, tel qu'enregistré en base pour ce projet. */
  protected async fetchContactedByPhone(configId: string): Promise<boolean | undefined> {
    return this.get<boolean | undefined>(`${this.apiUrl}Order/ContactedByPhone`, new HttpParams().set('id', configId));
  }

  /** Met à jour en base le consentement du client à être contacté par téléphone pour ce projet. */
  protected async putContactedByPhone(configId: string, contactedByPhone: boolean): Promise<void> {
    await this.put(
      `${this.apiUrl}Order/ContactedByPhone`,
      {},
      new HttpParams().set('id', configId).set('contactedByPhone', contactedByPhone)
    );
  }

  /**
   * Identifiant de la configuration actuellement chargée, source du consentement téléphonique
   * pour {@link getContactedByPhone} et {@link updateContactedByPhone}. Surchargée par
   * {@link BaseAppService} et par les services pilotés par un état de tunnel dédié.
   *
   * Ce n'est jamais le `configurationId` d'un état de tunnel (id de l'entité
   * `TunnelConfiguration`, utilisé pour les endpoints `Tunnel/*`) : c'est le véritable
   * `Configuration.Id` (ex. `kitchenConfigurationId`, `bathroomConfigurationId`…), seul id
   * valide pour les endpoints `Order/ContactedByPhone`. Les deux ids coexistent sur le même
   * objet d'état et sont faciles à confondre.
   */
  protected get currentConfigurationIdForContactedByPhone(): string | undefined {
    return undefined;
  }

  /** Consentement du client à être contacté par téléphone, tel qu'enregistré en base pour ce projet. */
  public async getContactedByPhone(): Promise<boolean | undefined> {
    const configId = this.currentConfigurationIdForContactedByPhone;
    return configId ? this.fetchContactedByPhone(configId) : undefined;
  }

  /** Met à jour le consentement du client à être contacté par téléphone pour ce projet. */
  public async updateContactedByPhone(contactedByPhone: boolean): Promise<void> {
    const configId = this.currentConfigurationIdForContactedByPhone;
    if (!configId) {
      return;
    }

    await this.putContactedByPhone(configId, contactedByPhone);
    this.setPersistedContactedByPhone(configId, contactedByPhone);
  }

  /**
   * Fonction de récupération des orders
   * @returns Promise
   */
  getOrders(id: string): Promise<Order[]> {
    const params = new HttpParams().set('id', id);
    return this.get<Order[]>(`${this.apiUrl}Order`, params);
  }

  /**
   * Fonction de mise à jour des orders
   * @returns Promise
   */
  updateOrder(order: Order): Promise<void> {
    return this.put(`${this.apiUrl}Order`, order);
  }

  /**
   * Fonction d'envoi de la commande fournisseurs via le backend
   * @param orderId identifiant de la commande
   */
  sendSupplier(orderId: string): Promise<void> {
    const params = new HttpParams().set('orderId', orderId);
    return this.put(`${this.apiUrl}Order/SendSupplier`, null, params);
  }

  /**
   * Fonction d'envoi forcé de la commande fournisseurs via le backend.
   * Réservée aux administrateurs côté API.
   */
  forceSendSupplier(orderId: string): Promise<void> {
    const params = new HttpParams().set('orderId', orderId);
    return this.put(`${this.apiUrl}Order/ForceSendSupplier`, null, params);
  }

  saveOpportunityNumber(orderId: string, opportunityNumber: number): Promise<void> {
    return this.post(`${this.apiUrl}Order/AssignOpportunityNumber`, {
      orderId,
      opportunityNumber
    });
  }

  /**
   * Fonction de récupération des environnements
   * @returns Promise
   */
  getLocations<L extends Location>(): Promise<L[]> {
    return this.get<L[]>(`${this.apiUrl}Configuration/Locations`);
  }

  /**
   * Fonction de suppression d'un projet
   * @param id id de la configuration
   * @returns promise
   */
  deleteProjectById(id: string): Promise<any> {
    const params = new HttpParams().set('configurationId', id);
    return this.delete(`${this.apiUrl}Configuration`, undefined, params);
  }

  /**
   * Récupération des images du configurateur pour les remplacées
   * @returns Liste des images utilisées sur le projet
   */
  getAdminPictures<P extends Picture>(): Promise<P[]> {
    return this.get<P[]>(`${this.apiUrl}Data/Pictures`);
  }

  /**
   * Changement d'une image
   * @param id Identifiant unique d'une image
   * @param file Fichier de remplacement pour l'image
   * @returns
   */
  setAdminPicture(id: number, file: File): Promise<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    formData.append('id', id.toString());
    return this.post(`${this.apiUrl}Data/Pictures`, formData);
  }

  /**
   * Changement des prix
   * @param file Fichier de prix
   * @returns Résultat de l'import (ok ou pas)
   */
  setAdminPrices(file: File): Promise<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.post(`${this.apiUrl}Data`, formData);
  }


  /**
   * Permet de récupérer les EANs de promotions
   */
  getEANs(): Promise<Ean[]> {
    return this.get<Ean[]>(this.apiUrl + 'Data/EAN');
  }

  /**
   * Permet de récupérer les EANs de promotions
   */
  getDiscounts(): Promise<Discount[]> {
    return this.get<Discount[]>(this.apiUrl + 'Data/Discounts');
  }

  addDiscount(startDate: string, endDate: string, eanId: string): Promise<Discount[]> {
    const body = [
      {
        startDate: startDate,
        endDate: endDate,
        eanId: eanId,
      },
    ];

    return this.post<Discount[]>(this.apiUrl + 'Data/Discounts', body);
  }

  removeDiscount(discountId: string): Promise<Discount[]> {
    const params = new HttpParams().set('discountId', discountId);
    return this.delete<Discount[]>(this.apiUrl + 'Data/Discounts', params);
  }

  getCdmUrl(): Promise<Cdm> {
    const body: Parameters = {
      organisation: 'RACINE;ENS1;AAA;A1',
      userName: 'VEND-DOE',
    };

    return this.post<Cdm>(this.apiUrl + 'Administration/Cdm', body);
  }

  /**
   * Fonction d'ouverture de la modal d'alerte en cas d'erreur
   * @param message message de l'erreur
   */
  openErrorModal(message: string) {
    this.modalService.display(MODAL.GENERIC_ERROR, { errorMessage: message });
  }

  /**
   * Fonction d'ajout d'une liste de produits dans la configuration
   * @param productList
   * @returns
   */
  addProductListToConfiguration(productList: ShoppingCartProductList): void {
    return;
  }

  /**
   * Fonction de mise à jour du orderNumber et du supplierSent
   * @param product
   * @returns
   */
  updateProductOrder(product: ShoppingCartProduct): Promise<void> {
    return Promise.resolve();
  }
}
